import React, { useEffect, useState } from 'react';
import { supabase } from './lib/supabase';
import { CSVImport } from './components/CSVImport';
import { GoogleSheetsImport } from './components/GoogleSheetsImport';
import { GoogleSheetsAvailabilityImport } from './components/GoogleSheetsAvailabilityImport';
import { MatchingDashboard } from './components/MatchingDashboard';
import { FamilyAvailability } from './components/FamilyAvailability';
import { EmailManagement } from './components/EmailManagement';
import { DuplicateDetection } from './components/DuplicateDetection';
import { HamburgerMenu, ViewType } from './components/HamburgerMenu';
import { AuthProvider } from './contexts/AuthContext';
import { AuthGuard } from './components/Auth/AuthGuard';
import { useAuth } from './contexts/AuthContext';
import './App.css';

function AppContent() {
  const { user, signOut } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState<string>('Testing...');
  const [studentsCount, setStudentsCount] = useState<number>(0);
  const [familiesCount, setFamiliesCount] = useState<number>(0);
  const [currentView, setCurrentView] = useState<ViewType>('import');
  const [selectedSemesterId, setSelectedSemesterId] = useState<number | null>(null);
  const [selectedSeason, setSelectedSeason] = useState('Spring');
  const [selectedYear, setSelectedYear] = useState(2025);
  const [semesters, setSemesters] = useState<any[]>([]);

  useEffect(() => {
    loadSemestersAndDefault();
  }, []);

  const loadSemestersAndDefault = async () => {
    try {
      const [semestersResult, assignmentsResult] = await Promise.all([
        supabase.from('semesters').select('*').order('year', { ascending: false }).order('season'),
        supabase.from('assignments').select('semester_id')
      ]);

      if (semestersResult.data) {
        setSemesters(semestersResult.data);
        
        // Set to active semester or most recent semester with assignments
        const activeSemester = semestersResult.data.find(s => s.is_active);
        
        let defaultSemester = activeSemester;
        if (!defaultSemester && assignmentsResult.data) {
          // Get unique semester IDs from assignments
          const semesterIds = assignmentsResult.data.map(a => a.semester_id).filter(Boolean);
          const semesterIdsWithAssignments = Array.from(new Set(semesterIds));
          
          // Find the most recent semester that has assignments
          defaultSemester = semestersResult.data
            .filter(s => semesterIdsWithAssignments.includes(s.id))
            .sort((a, b) => {
              if (a.year !== b.year) return b.year - a.year;
              const seasonOrder: { [key: string]: number } = { 'Spring': 1, 'Summer': 2, 'Fall': 3 };
              return (seasonOrder[b.season] || 0) - (seasonOrder[a.season] || 0);
            })[0];
        }
        
        // Fall back to most recent semester
        if (!defaultSemester) {
          defaultSemester = semestersResult.data[0];
        }
        
        if (defaultSemester) {
          setSelectedSemesterId(defaultSemester.id);
          setSelectedSeason(defaultSemester.season);
          setSelectedYear(defaultSemester.year);
        }
      }
    } catch (error) {
      console.error('Error loading semesters:', error);
    }
  };

  useEffect(() => {
    loadCounts();
  }, []);

  const createOrFindSemester = async (season: string, year: number) => {
    const semesterName = `${season} ${year}`;
    
    // Check if semester exists
    let semester = semesters.find(s => s.season === season && s.year === year);
    
    if (!semester) {
      // Create new semester
      const { data, error } = await supabase.from('semesters').insert({
        name: semesterName,
        season,
        year
      }).select().single();
      
      if (error) throw error;
      if (data) {
        semester = data;
        setSemesters([...semesters, data]);
      }
    }
    
    return semester;
  };

  const handleSeasonYearChange = async (season: string, year: number) => {
    setSelectedSeason(season);
    setSelectedYear(year);
    
    // Find or create semester
    const semester = await createOrFindSemester(season, year);
    if (semester) {
      setSelectedSemesterId(semester.id);
    }
  };

  const loadCounts = async () => {
    try {
      const [studentsResult, familiesResult] = await Promise.all([
        supabase.from('students').select('*', { count: 'exact' }),
        supabase.from('families').select('*', { count: 'exact' })
      ]);

      if (studentsResult.error || familiesResult.error) {
        setConnectionStatus('Error loading data');
      } else {
        setStudentsCount(studentsResult.count || 0);
        setFamiliesCount(familiesResult.count || 0);
        setConnectionStatus('Connected!');
      }
    } catch (err) {
      setConnectionStatus(`Connection failed: ${err}`);
    }
  };

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-left">
          <h2 className="app-title">Student Matching App</h2>
          
          {/* Semester Picker */}
          <div className="semester-picker">
            <select 
              value={selectedSeason} 
              onChange={(e) => handleSeasonYearChange(e.target.value, selectedYear)}
              style={{ 
                padding: '4px 8px', 
                backgroundColor: '#333', 
                color: 'white',
                border: '1px solid #666',
                borderRadius: '4px',
                fontSize: '0.9em'
              }}
            >
              <option value="Spring">Spring</option>
              <option value="Summer">Summer</option>
              <option value="Fall">Fall</option>
            </select>
            
            <input 
              type="number" 
              value={selectedYear} 
              onChange={(e) => handleSeasonYearChange(selectedSeason, parseInt(e.target.value) || 2025)}
              min="2020" 
              max="2030"
              style={{ 
                padding: '4px 8px', 
                backgroundColor: '#333', 
                color: 'white',
                border: '1px solid #666',
                borderRadius: '4px',
                width: '70px',
                fontSize: '0.9em'
              }}
            />
          </div>
          
          <div className="status-info">
            <span style={{ color: connectionStatus === 'Connected!' ? '#28a745' : '#dc3545' }}>
              {connectionStatus}
            </span>
            <span style={{ margin: '0 10px', color: '#8b949e' }}>|</span>
            <span style={{ color: '#8b949e', marginRight: '10px' }}>
              {user?.email}
            </span>
            <button
              onClick={() => signOut()}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                color: '#dc3545',
                border: '1px solid #dc3545',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Sign Out
            </button>
          </div>
        </div>
        
        <HamburgerMenu 
          currentView={currentView}
          onViewChange={setCurrentView}
          studentsCount={studentsCount}
          familiesCount={familiesCount}
        />
      </header>
      
      <main style={{ width: '100%', height: 'calc(100vh - 60px)' }}>
        {currentView === 'import' ? (
          <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
            <h2>Import Data</h2>
            <div style={{ padding: '20px', border: '1px solid white', borderRadius: '8px', marginBottom: '30px' }}>
              <GoogleSheetsImport onImportComplete={loadCounts} selectedSemesterId={selectedSemesterId} />
            </div>
            <div style={{ padding: '20px', border: '1px solid white', borderRadius: '8px', marginBottom: '30px' }}>
              <GoogleSheetsAvailabilityImport onImportComplete={loadCounts} selectedSemesterId={selectedSemesterId} />
            </div>
            <div style={{ display: 'flex', gap: '30px', justifyContent: 'space-around', flexWrap: 'wrap' }}>
              <div style={{ padding: '20px', border: '1px solid white', borderRadius: '8px', minWidth: '250px' }}>
                <CSVImport type="families" onImportComplete={loadCounts} />
              </div>
              <div style={{ padding: '20px', border: '1px solid white', borderRadius: '8px', minWidth: '250px' }}>
                <CSVImport type="students" onImportComplete={loadCounts} />
              </div>
              <div style={{ padding: '20px', border: '1px solid white', borderRadius: '8px', minWidth: '250px' }}>
                <CSVImport type="assignments" onImportComplete={loadCounts} />
              </div>
            </div>
          </div>
        ) : currentView === 'availability' ? (
          <FamilyAvailability 
            selectedSemesterId={selectedSemesterId}
            selectedSeason={selectedSeason}
            selectedYear={selectedYear}
            onAvailabilityChange={loadCounts}
          />
        ) : currentView === 'duplicates' ? (
          <DuplicateDetection 
            onDuplicatesChanged={loadCounts}
          />
        ) : currentView === 'emails' ? (
          <EmailManagement 
            selectedSemesterId={selectedSemesterId}
            selectedSeason={selectedSeason}
            selectedYear={selectedYear}
          />
        ) : (
          <MatchingDashboard 
            selectedSemesterId={selectedSemesterId}
            selectedSeason={selectedSeason}
            selectedYear={selectedYear}
          />
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AuthGuard>
        <AppContent />
      </AuthGuard>
    </AuthProvider>
  );
}

export default App;
